import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { FC, memo, useState } from 'react';
import { useRequest } from 'ahooks';
import { Button, Form, message, Select, Space, Spin, Switch } from 'udesk-ui';
import Rules from './components/Rules';
import { InformationOutlined } from '@udesk/icons';
import { getTranslateConfigs, postTranslateConfigs } from 'src/api/translateConfigs';
import { putTranslateConfigsById } from 'src/api/translateConfigs/{id}';
import { TranslateConfigCreateRequest, TranslateConfigFoundResponse } from 'src/api/types';
import { enableTranslate } from './utils';

export interface FormModel
    extends Pick<TranslateConfigFoundResponse, 'dataSourceList' | 'ruleList'> {
    activeFlag?: boolean;
    termFlag?: boolean;
}

interface TemplateProps {}

const Template: FC<TemplateProps> = memo(() => {
    const [form] = Form.useForm<FormModel>();

    const {
        data: config,
        loading: getLoading,
        run: refresh,
    } = useRequest(() => getTranslateConfigs(), {
        onSuccess: (res) => {
            form.setFieldsValue({
                ...res.data,
                activeFlag: res.data?.activeFlag === 1,
                termFlag: res.data?.termFlag === 1,
            });
        },
    });

    const [saveLoading, setSaveLoading] = useState(false);

    const onFinish = (values: FormModel) => {
        const params: TranslateConfigCreateRequest = {
            ...values,
            activeFlag: values.activeFlag ? 1 : 0,
            termFlag: values.termFlag ? 1 : 0,
        };

        setSaveLoading(true);

        if (config?.data?.id) {
            putTranslateConfigsById(params, { segments: { id: config?.data?.id } })
                .then(() => {
                    message.success(
                        /* 保存成功 */ UdeskLocales['current'].pages.qaLanguageSetting.translate
                            .template.index.saveSuccessful
                    );
                    refresh();
                })
                .finally(() => {
                    setSaveLoading(false);
                });
        } else {
            postTranslateConfigs(params)
                .then(() => {
                    message.success(
                        /* 保存成功 */ UdeskLocales['current'].pages.qaLanguageSetting.translate
                            .template.index.saveSuccessful
                    );
                    refresh();
                })
                .finally(() => {
                    setSaveLoading(false);
                });
        }
    };

    return (
        <Form<FormModel>
            form={form}
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            onFinish={onFinish}
        >
            <Spin spinning={getLoading || saveLoading}>
                <Form.Item
                    label={
                        /* 开启状态 */ UdeskLocales['current'].pages.qaLanguageSetting.translate
                            .template.index.onState
                    }
                    name="activeFlag"
                    valuePropName="checked"
                    extra={
                        <Space>
                            <InformationOutlined />
                            <span>
                                {/* 将源语言数据翻译为目标语言后，再进行质检及智能标签。 */}
                                {
                                    UdeskLocales['current'].pages.qaLanguageSetting.translate
                                        .template.index
                                        .translateTheSourceLanguageDataIntoTheTargetLanguageAndThenPerformQualityInspectionAndSmartLabel
                                }
                            </span>
                        </Space>
                    }
                >
                    <Switch />
                </Form.Item>

                <Form.Item
                    label={
                        /* 翻译术语 */ UdeskLocales['current'].pages.qaLanguageSetting.translate
                            .template.index.translationOfTerms
                    }
                    name="termFlag"
                    valuePropName="checked"
                    extra={
                        <Space>
                            <InformationOutlined />
                            <span>
                                {/* 可在语义智能 -
                                智能词库设置翻译术语，开启后，可将转写结果中的指定词语翻译为专业术语。 */}
                                {
                                    UdeskLocales['current'].pages.qaLanguageSetting.translate
                                        .template.index
                                        .availableInSemanticIntelligenceSetTheTranslationTermInTheSmartThesaurusWhenEnabledTheSpecifiedWordsInTheTranscriptionResultCanBeTranslatedIntoProfessionalTerms
                                }
                            </span>
                        </Space>
                    }
                >
                    <Switch />
                </Form.Item>

                <Form.Item<FormModel>
                    noStyle
                    shouldUpdate={(pre, cur) => pre.activeFlag !== cur.activeFlag}
                >
                    {({ getFieldsValue }) => {
                        const activeFlag = getFieldsValue().activeFlag;

                        return activeFlag ? (
                            <>
                                <Form.Item
                                    label={
                                        /* 数据源类型 */ UdeskLocales['current'].pages
                                            .qaLanguageSetting.translate.template.index
                                            .dataSourceType
                                    }
                                    name="dataSourceList"
                                >
                                    <Select
                                        options={Udesk.enums.inspectDataSources
                                            .filter((d) => enableTranslate(d.id))
                                            .map((d) => {
                                                return {
                                                    value: d.id,
                                                    label: d.name,
                                                };
                                            })}
                                        mode="multiple"
                                        maxTagCount={3}
                                        style={{ width: 500 }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label={
                                        /* 翻译规则 */ UdeskLocales['current'].pages
                                            .qaLanguageSetting.translate.template.index
                                            .translationRules
                                    }
                                    name="ruleList"
                                >
                                    <Rules />
                                </Form.Item>
                            </>
                        ) : null;
                    }}
                </Form.Item>

                <Form.Item wrapperCol={{ offset: 4 }}>
                    <Button type="primary" htmlType="submit">
                        {/* 保存 */}
                        {
                            UdeskLocales['current'].pages.qaLanguageSetting.translate.template.index
                                .preserve
                        }
                    </Button>
                </Form.Item>
            </Spin>
        </Form>
    );
});

export default Template;
